import { Component } from '@theme/component';
import { debounce, preloadImage } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #highResImagesLoaded = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);

    // Lock background scroll when dialog opens
    this.observer = new MutationObserver(() => {
      if (this.refs.dialog.hasAttribute('open')) {
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        this.#ensureCounter();
      } else {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    });

    this.observer.observe(this.refs.dialog, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  /* ---- Counter ---- */
  /* Fix thumbnail scroll — intercept wheel events on container */
  #fixThumbnailScroll() {
    const container = this.refs.dialog.querySelector('.dialog-thumbnails-list-container');
    if (!container) return;

    // Force inline styles at highest priority
    container.style.setProperty('overflow-y', 'scroll', 'important');
    container.style.setProperty('height', '100vh', 'important');
    container.style.setProperty('max-height', '100vh', 'important');

    const list = container.querySelector('.dialog-thumbnails-list');
    if (list) {
      list.style.setProperty('height', 'auto', 'important');
      list.style.setProperty('overflow', 'visible', 'important');
      list.style.setProperty('display', 'flex', 'important');
      list.style.setProperty('flex-direction', 'column', 'important');
    }

    // KEY FIX: dialog element absorbs wheel events — stop propagation
    // so the thumbnail container handles its own scrolling
    if (!container._wheelListenerAdded) {
      container._wheelListenerAdded = true;
      container.addEventListener('wheel', (e) => {
        e.stopPropagation();
        container.scrollTop += e.deltaY;
        e.preventDefault();
      }, { passive: false });

      // Touch scroll support for thumbnail sidebar
      let touchStartY = 0;
      container.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
      }, { passive: true });

      container.addEventListener('touchmove', (e) => {
        e.stopPropagation();
        const delta = touchStartY - e.touches[0].clientY;
        container.scrollTop += delta;
        touchStartY = e.touches[0].clientY;
        e.preventDefault();
      }, { passive: false });
    }
  }

  #ensureCounter() {
    if (this.refs.dialog.querySelector('.zoom-dialog-counter')) return;
    const counter = document.createElement('div');
    counter.className = 'zoom-dialog-counter';
    counter.setAttribute('aria-live', 'polite');
    this.refs.dialog.appendChild(counter);
  }

  #updateCounter(index) {
    const counter = this.refs.dialog.querySelector('.zoom-dialog-counter');
    if (!counter) return;
    const total = this.refs.media ? this.refs.media.length : 0;
    counter.textContent = `${index + 1} / ${total}`;
  }

  /* ---- Open ---- */
  async open(index, event) {
    event.preventDefault();
    this.refs.dialog.showModal();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.#fixThumbnailScroll();
          this.selectThumbnail(index, { behavior: 'instant' });
          this.#ensureCounter();
          this.#updateCounter(index);
        }, 30);
      });
    });
  }

  /* ---- Prev / Next ---- */
  handlePrev() {
    const { media } = this.refs;
    const activeIndex = this.#getActiveIndex();
    const newIndex = activeIndex > 0 ? activeIndex - 1 : media.length - 1;
    this.selectThumbnail(newIndex);
  }

  handleNext() {
    const { media } = this.refs;
    const activeIndex = this.#getActiveIndex();
    const newIndex = activeIndex < media.length - 1 ? activeIndex + 1 : 0;
    this.selectThumbnail(newIndex);
  }

  #getActiveIndex() {
    const { thumbnails } = this.refs;
    return Array.from(thumbnails.querySelectorAll('button')).findIndex(
      (b) => b.getAttribute('aria-selected') === 'true'
    );
  }

  /* ---- Select thumbnail ---- */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;
    if (!media[index]) return;

    // Update aria-selected on all thumbnail buttons
    Array.from(thumbnails.querySelectorAll('button')).forEach((btn, i) => {
      btn.setAttribute('aria-selected', `${i === index}`);
    });

    // Scroll main gallery using scrollLeft
    const gallery = this.refs.dialog.querySelector('.dialog-zoomed-gallery');
    if (gallery) {
      const itemWidth = gallery.offsetWidth;
      gallery.scrollTo({
        left: itemWidth * index,
        behavior: options.behavior,
      });
    }

    // Scroll active thumbnail into view in sidebar
    const thumbBtns = Array.from(thumbnails.querySelectorAll('button'));
    if (thumbBtns[index]) {
      const container = this.refs.dialog.querySelector('.dialog-thumbnails-list-container');
      if (container) {
        const thumbTop = thumbBtns[index].offsetTop;
        container.scrollTo({
          top: thumbTop - container.offsetHeight / 2 + thumbBtns[index].offsetHeight / 2,
          behavior: options.behavior,
        });
      }
    }

    this.#updateCounter(index);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /* ---- Sync on manual scroll/swipe ---- */
  handleScroll = debounce(() => {
    const gallery = this.refs.dialog.querySelector('.dialog-zoomed-gallery');
    if (!gallery) return;

    const { media, thumbnails } = this.refs;
    const itemWidth = gallery.offsetWidth;
    if (!itemWidth) return;

    const index = Math.round(gallery.scrollLeft / itemWidth);
    if (index < 0 || index >= media.length) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((btn, i) => {
      btn.setAttribute('aria-selected', `${i === index}`);
    });

    const container = this.refs.dialog.querySelector('.dialog-thumbnails-list-container');
    const thumbBtns = Array.from(thumbnails.querySelectorAll('button'));
    if (thumbBtns[index] && container) {
      const thumbTop = thumbBtns[index].offsetTop;
      container.scrollTo({
        top: thumbTop - container.offsetHeight / 2 + thumbBtns[index].offsetHeight / 2,
        behavior: 'smooth',
      });
    }

    this.#updateCounter(index);
  }, 100);

  /* ---- Keyboard ---- */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.handlePrev();
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.handleNext();
    }
  }

  /* ---- Thumbnail events ---- */
  handleThumbnailClick(index) {
    this.selectThumbnail(index);
  }

  handleThumbnailPointerEnter(index) {}

  /* ---- Close ---- */
  close() {
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}